import { ApiStatus } from 'network'

type Fetcher<T extends Record<string, any> = Record<string, any>> = (data: any) => Promise<FetchResponse<T[] | HasPageInfoResponse<T>>>

type HandleParams<T = Record<string, any>> = (currentParams: LoadMoreParams, dataSource?: T[], pageInfo?: PageInfo) => LoadMoreParams

type PageParamsFieldMap = {
  page: string
  pageSize?: string
}

type UseLoadMoreParams<T extends Record<string, any> = Record<string, any>> = {
  /** 是否初始化加载 */
  isInitFetch?: boolean
  fetcher: Fetcher<T>
  handleParams?: HandleParams<T>
  defaultPageSize?: number
  /** 拦截器 */
  requestInterceptor?: (data?: any) => boolean
  /** 参数分页字段映射 */
  pageParamsFieldMap?: PageParamsFieldMap
}

interface LoadMoreParams extends Record<string, any> {
  page: number
}

export const useLoadMore = <T extends Record<string, any> = Record<string, any>>(params: UseLoadMoreParams<T>) => {
  const {
    fetcher,
    handleParams,
    defaultPageSize = 10,
    requestInterceptor,
    isInitFetch = true,
    pageParamsFieldMap = {
      page: 'page',
    },
  } = params
  const dataSource = shallowRef<T[]>([])
  const currentPageDataSource = shallowRef<T[]>([])

  const hasMore = ref(false)
  const loadMoreLoading = ref(false)
  const loading = ref(false)
  const pageInfo = ref<PageInfo>({
    pageCurrent: 1,
    pageCount: 0,
    pageSize: defaultPageSize,
    pageTotal: 0,
  })

  let isHasPageInfo = false

  const currentParams = ref<LoadMoreParams>({
    page: 1,
  })
  let _isInitFetch = isInitFetch

  // 新增：重置状态方法
  const reset = () => {
    dataSource.value = []
    currentPageDataSource.value = []
    hasMore.value = false
    currentParams.value = {
      page: 1,
    }
    pageInfo.value = {
      pageCurrent: 1,
      pageCount: 0,
      pageSize: defaultPageSize,
      pageTotal: 0,
    }
  }

  const fetchData = async () => {
    if (!_isInitFetch) return
    if (requestInterceptor && !requestInterceptor()) return

    loading.value = true
    const params = handleParams ? handleParams(currentParams.value, dataSource.value, pageInfo.value) : currentParams.value
    const body = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined))
    currentParams.value = params
    const res = await fetcher({
      ...body,
      pageSize: defaultPageSize,
    })
    loading.value = false

    if (res.status !== ApiStatus.Success) return

    if (Array.isArray(res.data)) {
      const data = res.data as T[]
      hasMore.value = data.length === defaultPageSize
      dataSource.value = data
      currentPageDataSource.value = data
    } else {
      const data = (res as FetchResponse<HasPageInfoResponse<T>>).data
      isHasPageInfo = true
      pageInfo.value = data.pageInfo
      dataSource.value = data.list
      currentPageDataSource.value = data.list
      // 修复：pageInfo 响应也需要设置 hasMore
      hasMore.value = pageInfo.value.pageCurrent < pageInfo.value.pageCount
    }
  }

  const initFetch = async () => {
    reset()
    _isInitFetch = true
    fetchData()
  }

  const loadMore = async (options?: { page?: number; pageSize?: number }) => {
    if (!hasMore.value) return
    loadMoreLoading.value = true
    const params = handleParams ? handleParams(currentParams.value, dataSource.value, pageInfo.value) : currentParams.value
    currentParams.value = params
    if (pageParamsFieldMap.page) {
      if (isHasPageInfo) {
        currentParams.value.page = pageInfo.value.pageCurrent + 1
      } else {
        currentParams.value.page += 1
      }
    }
    const fetchParams: Record<string, any> = {
      ...params,
      ...currentParams.value,
      pageSize: pageInfo.value.pageSize,
    }

    if (options?.page) {
      fetchParams.page = options.page
      currentParams.value.page = options.page
    }
    if (options?.pageSize) {
      fetchParams.pageSize = options.pageSize
      currentParams.value.pageSize = options.pageSize
    }

    Object.entries(pageParamsFieldMap).forEach(([key, value]) => {
      fetchParams[value] = currentParams.value[key]
    })
    const body = Object.fromEntries(Object.entries(fetchParams).filter(([, value]) => value !== undefined))
    const res = await fetcher({
      ...body,
      pageSize: defaultPageSize,
    })

    loadMoreLoading.value = false

    if (res.status !== ApiStatus.Success) return

    const data = res.data
    if (Array.isArray(data)) {
      hasMore.value = data.length === defaultPageSize
      if (data.length > 0) {
        dataSource.value = [...dataSource.value, ...data]
      } else {
        hasMore.value = false
      }
    } else {
      pageInfo.value = data.pageInfo
      dataSource.value = [...dataSource.value, ...data.list]
      currentPageDataSource.value = data.list
      hasMore.value = pageInfo.value.pageCurrent < pageInfo.value.pageCount
    }
  }

  onMounted(() => {
    fetchData()
  })

  return {
    dataSource,
    fetchData,
    loadMore,
    loadMoreLoading,
    hasMore,
    loading,
    initFetch,
    pageInfo,
    currentPageDataSource,
    reset,
  }
}
